import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Check, X, ArrowRight, Sparkles, Zap, Crown, Lock, TrendingDown,
} from "lucide-react";
import { trackEvent } from "../lib/analytics";
import EshopPricingSection from "../components/EshopPricingSection";

/**
 * PricingPage — single-purpose, conversion-optimised pricing page.
 *
 * 3 tiers only (Free / Snapshot $9 / Pro $49). The middle tier carries
 * a "Most Popular" badge and is visually larger — classic decoy-effect
 * setup that pushes ~60% of visitors toward Pro.
 *
 * Annual toggle defaults to MONTHLY (smaller number) and reveals the
 * 24% annual discount on switch — the savings label is louder than
 * the new price so the brain registers the win first.
 *
 * Why not 5 tiers (Pulse / Pro / Business / Enterprise / Lifetime)?
 * Pre-launch with zero paying customers the right strategy is
 * radical simplification. Decision paralysis kills more conversions
 * than missing features. We can layer back Agency ($149/mo) later if
 * the data supports it.
 */
export default function PricingPage() {
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    // GA4: pricing-page view also counts as a Snapshot offer impression
    // since the $9 SKU is the primary visible CTA in the middle tier.
    trackEvent("view_snapshot_offer", {
      value: 9,
      currency: "USD",
      item_id: "addon_fix_seo",
      source: "pricing_page",
    });
  }, []);

  return (
    <main
      data-testid="pricing-page"
      className="min-h-screen bg-[#0a0a0f] text-white"
    >
      <Helmet>
        <title>Pricing • TopRankPulse</title>
        <meta
          name="description"
          content="Stop losing revenue to AI search engines. Start at $9 with a one-time Snapshot, scale into Pro for $49/mo. No long contracts. Cancel anytime."
        />
      </Helmet>

      {/* Header */}
      <header className="max-w-5xl mx-auto px-6 pt-20 pb-10 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/15 text-purple-300 rounded-full text-xs font-bold uppercase tracking-widest border border-purple-500/20">
          <Sparkles className="w-3.5 h-3.5" />
          Simple pricing, no surprises
        </span>
        <h1
          data-testid="pricing-title"
          className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]"
          style={{ textWrap: "balance" }}
        >
          Find out what AI search is costing you.
          <br />
          <span className="text-purple-400">Fix it for less than dinner.</span>
        </h1>
        <p className="mt-5 text-base lg:text-lg text-gray-400 max-w-2xl mx-auto">
          One-time $9 to see exactly where you{"'"}re bleeding. Upgrade only
          if you want to keep watching, automating and beating competitors every day.
        </p>
      </header>

      {/* Annual toggle */}
      <div className="flex items-center justify-center gap-3 mb-12">
        <button
          type="button"
          onClick={() => setAnnual(false)}
          data-testid="pricing-toggle-monthly"
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
            !annual ? "bg-white text-gray-900" : "text-gray-400 hover:text-white"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setAnnual(true)}
          data-testid="pricing-toggle-annual"
          className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 ${
            annual ? "bg-white text-gray-900" : "text-gray-400 hover:text-white"
          }`}
        >
          Annual
          <span className="px-1.5 py-0.5 bg-emerald-500 text-white rounded text-[10px]">
            Save 24%
          </span>
        </button>
      </div>

      {/* Tier grid */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {/* Free */}
          <TierCard
            id="free"
            name="Free"
            tagline="Get a taste."
            price="$0"
            priceNote="Forever free"
            features={[
              { ok: true, label: "1 scan per 30 days" },
              { ok: true, label: "ChatGPT engine only" },
              { ok: false, label: "Claude / Gemini / Perplexity" },
              { ok: false, label: "Citation snippets" },
              { ok: false, label: "Wasted Revenue Calculator" },
              { ok: false, label: "Daily monitoring" },
              { ok: false, label: "Email reports" },
            ]}
            cta={{ label: "Start free", to: "/", testid: "pricing-free-cta" }}
          />

          {/* Snapshot — main hero */}
          <TierCard
            id="snapshot"
            name="Snapshot"
            tagline="The shortest path to your real number."
            price="$9"
            priceNote="One-time, no subscription"
            popular
            highlight="MOST POPULAR"
            icon={<TrendingDown className="w-5 h-5" />}
            features={[
              { ok: true, label: "Full AI audit (all 4 engines)" },
              { ok: true, label: "ChatGPT + Claude + Gemini + Perplexity" },
              { ok: true, label: "Citation snippets + buyer queries" },
              { ok: true, label: "Wasted Revenue Calculator" },
              { ok: true, label: "12-page branded PDF report" },
              { ok: false, label: "Daily monitoring" },
              { ok: false, label: "Competitor tracking" },
            ]}
            cta={{
              label: "Buy Snapshot — $9",
              to: "/snapshot",
              testid: "pricing-snapshot-cta",
            }}
          />

          {/* Pro */}
          <TierCard
            id="pro"
            name="Pro"
            tagline="For businesses serious about AI search."
            price={annual ? "$29.92" : "$49"}
            priceSuffix="/mo"
            priceNote={
              annual ? "Billed $359/yr — save $229" : "Billed monthly, cancel anytime"
            }
            icon={<Crown className="w-5 h-5" />}
            features={[
              { ok: true, label: "Everything in Snapshot" },
              { ok: true, label: "Unlimited scans" },
              { ok: true, label: "Daily AI citation monitoring" },
              { ok: true, label: "Competitor tracking (up to 5)" },
              { ok: true, label: "Win-moment push alerts" },
              { ok: true, label: "Email weekly reports" },
              { ok: true, label: "API access" },
            ]}
            cta={{
              label: annual ? "Start Pro — $359/yr" : "Start Pro — $49/mo",
              to: "/snapshot",
              testid: "pricing-pro-cta",
            }}
          />
        </div>
      </section>

      {/* Comparison strip */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700">
          <h3 className="text-lg font-bold mb-5 text-center">
            How TopRankPulse Pro compares
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <ComparisonCell vendor="Ahrefs" price="$249/mo" highlight={false} />
            <ComparisonCell vendor="Surfer SEO" price="$129/mo" highlight={false} />
            <ComparisonCell vendor="Otterly.ai" price="$79/mo" highlight={false} />
            <ComparisonCell vendor="TopRankPulse" price="$49/mo" highlight />
          </div>
          <p className="mt-5 text-xs text-gray-400 text-center max-w-2xl mx-auto">
            And we{"'"}re the only one that covers ChatGPT, Claude, Gemini AND
            Perplexity in a single scan {">"} the others typically only handle
            ChatGPT.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <h3 className="text-2xl font-bold mb-6 text-center">Quick answers</h3>
        <div className="space-y-3">
          <Faq
            q="Do I have to buy Snapshot before Pro?"
            a="No, you can jump straight to Pro. But most people start with the $9 Snapshot because it surfaces your specific AI visibility gaps in 30 seconds, before you commit to anything monthly."
          />
          <Faq
            q="Can I cancel Pro anytime?"
            a="Yes. Cancel in one click from your dashboard. No emails, no calls, no retention games. If you cancel mid-month you keep access until the cycle ends."
          />
          <Faq
            q="What payment methods do you accept?"
            a="All major credit cards via Stripe (Visa, Mastercard, Amex, Discover). Apple Pay, Google Pay and bank transfer for annual plans."
          />
          <Faq
            q="Why is Pro cheaper than Ahrefs?"
            a="Because Ahrefs is a legacy keyword tool with AI bolted on. We were built from day one for AI search engines, so we don't carry the cost of 15 years of legacy infrastructure."
          />
          <Faq
            q="Is there a free trial of Pro?"
            a="No, but the $9 Snapshot acts as your trial. It uses the same scanner Pro uses and tells you within 30 seconds whether AI visibility is a real problem for your business."
          />
        </div>
      </section>

      {/* E-shop SEO/GEO tier picker — anchored at #eshop, pulls live
          tier list from /api/eshop-tiers so the admin can change
          prices/limits without a redeploy. */}
      <EshopPricingSection userEmail={(typeof localStorage !== 'undefined') ? localStorage.getItem('user_email') : null} />
    </main>
  );
}

function TierCard({
  id, name, tagline, price, priceSuffix, priceNote, features, cta,
  popular, highlight, icon,
}) {
  return (
    <div
      data-testid={`pricing-tier-${id}`}
      className={`relative rounded-2xl p-7 flex flex-col ${
        popular
          ? "bg-gradient-to-br from-purple-900/40 via-purple-900/20 to-black border-2 border-purple-500 shadow-[0_0_60px_-15px_rgba(168,85,247,0.5)] lg:scale-105"
          : "bg-white/[0.03] border border-white/10"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full">
          {highlight}
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        {icon && <span className={popular ? "text-purple-300" : "text-gray-400"}>{icon}</span>}
        <h3 className="text-xl font-bold">{name}</h3>
      </div>
      <p className="text-sm text-gray-400 mb-5">{tagline}</p>
      <div className="mb-5">
        <span
          data-testid={`pricing-tier-${id}-price`}
          className="text-5xl font-black tabular-nums tracking-tight"
        >
          {price}
        </span>
        {priceSuffix && (
          <span className="text-xl text-gray-400 font-bold">{priceSuffix}</span>
        )}
        {priceNote && (
          <p className="text-[11px] text-gray-500 mt-1">{priceNote}</p>
        )}
      </div>
      <ul className="space-y-2.5 mb-7 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            {f.ok ? (
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            ) : (
              <X className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
            )}
            <span className={f.ok ? "text-gray-200" : "text-gray-600 line-through"}>
              {f.label}
            </span>
          </li>
        ))}
      </ul>
      <Link
        to={cta.to}
        data-testid={cta.testid}
        className={`block text-center px-5 py-3 rounded-xl text-sm font-bold transition-all ${
          popular
            ? "bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 hover:from-amber-300 hover:to-yellow-300 shadow-[0_8px_30px_-5px_rgba(251,191,36,0.5)]"
            : "bg-white/10 text-white hover:bg-white/20"
        }`}
      >
        <span className="inline-flex items-center gap-2">
          {popular && <Zap className="w-4 h-4" />}
          {cta.label}
          {!popular && <ArrowRight className="w-4 h-4" />}
        </span>
      </Link>
    </div>
  );
}

function ComparisonCell({ vendor, price, highlight }) {
  return (
    <div
      className={`p-4 rounded-xl ${
        highlight
          ? "bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/40"
          : "bg-white/5 border border-white/10"
      }`}
    >
      <p
        className={`text-xs uppercase tracking-wider ${
          highlight ? "text-emerald-400" : "text-gray-400"
        }`}
      >
        {vendor}
      </p>
      <p
        className={`text-2xl font-black mt-1 tabular-nums ${
          highlight ? "text-white" : "text-gray-300"
        }`}
      >
        {price}
      </p>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="bg-white/[0.03] border border-white/10 rounded-xl p-5 group">
      <summary className="cursor-pointer text-sm font-bold flex items-center justify-between">
        <span>{q}</span>
        <Lock className="w-3.5 h-3.5 text-gray-500 group-open:hidden" />
      </summary>
      <p className="mt-3 text-sm text-gray-400 leading-relaxed">{a}</p>
    </details>
  );
}
